import React, { useState } from 'react';
import { 
  Button, 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  DataTable, 
  Select, 
  StatusBadge, 
  TextInput,
  toast
} from '@etherisc/ui-kit';
import { FileTextIcon, PlusIcon, TrashIcon, DownloadIcon, EditIcon, SearchIcon, PlayIcon, XIcon } from 'lucide-react';
import { useDocumentTemplates } from '../../hooks/useDocumentTemplates';
import { DocumentTemplateUploadDialog } from '../../components/documentTemplate/DocumentTemplateUploadDialog';
import { TestTemplateDialog } from '../../components/documentTemplate/TestTemplateDialog';
import type { 
  DocumentTemplate,
  DocumentTemplateUploadData
} from '../../types/documentTemplate';
import { 
  DocumentTemplateType, 
  DocumentTemplateStatus,
  getTypeLabel,
  getStatusLabel,
  formatFileSize
} from '../../types/documentTemplate';

// StatusBadge variant type
type StatusBadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

export function DocumentTemplateManagement() {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [templateToTest, setTemplateToTest] = useState<DocumentTemplate | null>(null);
  const [templateToEdit, setTemplateToEdit] = useState<DocumentTemplate | null>(null);
  const [editForm, setEditForm] = useState<{ status: DocumentTemplateStatus; tags: string }>({
    status: DocumentTemplateStatus.DRAFT,
    tags: ''
  });
  const [localFilters, setLocalFilters] = useState({
    search: '',
    type: 'all',
    status: 'all',
  });

    const {
    templates, 
    loading, 
    error, 
    total,
    page,
    totalPages,
    limit,
    createTemplate,
    updateTemplate, 
    deleteTemplate, 
    downloadTemplate,
    setFilters
  } = useDocumentTemplates();

  // Handle filter changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFilters = { ...localFilters, search: e.target.value };
    setLocalFilters(newFilters);
    setFilters({ 
      search: e.target.value || undefined,
      type: newFilters.type === 'all' ? undefined : (newFilters.type as DocumentTemplateType),
      status: newFilters.status === 'all' ? undefined : (newFilters.status as DocumentTemplateStatus)
    });
  };

  const handleTypeFilter = (value: string) => {
    const newFilters = { ...localFilters, type: value };
    setLocalFilters(newFilters);
    setFilters({ 
      search: newFilters.search || undefined,
      type: value === 'all' ? undefined : (value as DocumentTemplateType),
      status: newFilters.status === 'all' ? undefined : (newFilters.status as DocumentTemplateStatus)
    });
  };

  const handleStatusFilter = (value: string) => {
    const newFilters = { ...localFilters, status: value };
    setLocalFilters(newFilters);
    setFilters({ 
      search: newFilters.search || undefined,
      type: newFilters.type === 'all' ? undefined : (newFilters.type as DocumentTemplateType),
      status: value === 'all' ? undefined : (value as DocumentTemplateStatus)
    });
  };

  // Handle template actions
  const handleDownload = async (template: DocumentTemplate) => {
    try {
      await downloadTemplate(template.id);
      toast.success('Template downloaded successfully');
    } catch (err) {
      console.error('Failed to download template:', err);
      toast.error('Failed to download template');
    }
  };

  const handleEdit = (template: DocumentTemplate) => {
    setTemplateToEdit(template);
    setEditForm({
      status: template.status,
      tags: template.tags?.join(', ') || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!templateToEdit) return;

    try {
      const tagsArray = editForm.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      await updateTemplate(templateToEdit.id, {
        status: editForm.status,
        tags: tagsArray
      });

      toast.success('Template updated successfully');
      setEditDialogOpen(false);
      setTemplateToEdit(null);
    } catch (err) {
      console.error('Failed to update template:', err);
      toast.error('Failed to update template');
    }
  };

  const handleTest = (template: DocumentTemplate) => {
    setTemplateToTest(template);
    setTestDialogOpen(true);
  };

  const handleDelete = async (template: DocumentTemplate) => {
    if (window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      try {
        await deleteTemplate(template.id);
        // toast.success('Template deleted successfully'); // Removed toast
      } catch (err) {
        console.error('Failed to delete template:', err);
        // toast.error('Failed to delete template'); // Removed toast
      }
    }
  };

  const handleUpload = async (data: DocumentTemplateUploadData) => {
    try {
      await createTemplate(data); // Removed createTemplate
      toast.success('Template uploaded successfully');
      setUploadDialogOpen(false);
    } catch (error) {
      toast.error('Failed to upload template');
      throw error;
    }
  };

  // Status badge variant mapping
  const getStatusVariant = (status: DocumentTemplateStatus): StatusBadgeVariant => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DRAFT':
        return 'warning';
      case 'ARCHIVED':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  // Data table columns - not memoized to avoid stale closure issues with handlers
  const columns = [
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row: DocumentTemplate) => row.name,
      enableSorting: true,
      cell: ({ row }: { row: { original: DocumentTemplate } }) => (
        <div className="flex items-center space-x-2">
          <FileTextIcon className="w-4 h-4 text-blue-600" />
          <div>
            <div className="font-medium">{row.original.name}</div>
            {row.original.description && (
              <div className="text-sm text-gray-500">{row.original.description}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      accessorFn: (row: DocumentTemplate) => row.type,
      enableSorting: true,
      cell: ({ row }: { row: { original: DocumentTemplate } }) => (
        <span className="text-sm">{getTypeLabel(row.original.type)}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row: DocumentTemplate) => row.status,
      enableSorting: true,
      cell: ({ row }: { row: { original: DocumentTemplate } }) => (
        <StatusBadge variant={getStatusVariant(row.original.status)}>
          {getStatusLabel(row.original.status)}
        </StatusBadge>
      ),
    },
    {
      id: 'currentVersion',
      header: 'Version',
      cell: ({ row }: { row: { original: DocumentTemplate } }) => (
        <div className="text-sm">
          {row.original.currentVersion ? (
            <div>
              <div>v{row.original.currentVersion.version}</div>
              <div className="text-gray-500">
                {formatFileSize(row.original.currentVersion.fileSize)}
              </div>
            </div>
          ) : (
            <span className="text-gray-400">No version</span>
          )}
        </div>
      ),
    },
    {
      id: 'tags',
      header: 'Tags',
      cell: ({ row }: { row: { original: DocumentTemplate } }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.tags?.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded"
            >
              {tag}
            </span>
          ))}
          {(row.original.tags?.length || 0) > 3 && (
            <span className="text-xs text-gray-500">
              +{(row.original.tags?.length || 0) - 3} more
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessorFn: (row: DocumentTemplate) => row.createdAt,
      enableSorting: true,
      cell: ({ row }: { row: { original: DocumentTemplate } }) => (
        <div className="text-sm">
          {new Date(row.original.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }: { row: { original: DocumentTemplate } }) => (
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleDownload(row.original); }}
            disabled={!row.original.currentVersion}
          >
            <DownloadIcon className="w-4 h-4" />
          </Button>
          {row.original.type === 'EMAIL' && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleTest(row.original); }}
              title="Test Template"
            >
              <PlayIcon className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleEdit(row.original); }}
          >
            <EditIcon className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleDelete(row.original); }}
            className="text-red-600 hover:text-red-700"
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    ...Object.values(DocumentTemplateType).map(type => ({
      value: type,
      label: getTypeLabel(type),
    })),
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    ...Object.values(DocumentTemplateStatus).map(status => ({
      value: status,
      label: getStatusLabel(status),
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Templates</h1>
          <p className="text-gray-600">Manage document templates for quotes, policies, and reports</p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Upload Template
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SearchIcon className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextInput
              placeholder="Search templates..."
              value={localFilters.search}
              onChange={handleSearchChange}
            />
            <Select
              value={localFilters.type}
              options={typeOptions}
              onValueChange={handleTypeFilter}
            />
            <Select
              value={localFilters.status}
              options={statusOptions}
              onValueChange={handleStatusFilter}
            />
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Card>
          <CardContent>
            <div className="text-red-600 text-center py-4">
              {error}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates Table */}
      <DataTable
        columns={columns}
        data={templates}
        loading={loading}
        enableSorting={true}
        manualPagination={true}
        pageCount={totalPages}
        rowCount={total}
        onPaginationChange={(paginationState) => {
          const newPage = paginationState.pageIndex + 1;
          const newPageSize = paginationState.pageSize;
          setFilters({ page: newPage, limit: newPageSize });
        }}
        state={{
          pagination: {
            pageIndex: page - 1,
            pageSize: limit,
          },
        }}
        pagination={{
          showSizeSelector: true,
          showPageInfo: true,
          showNavigation: true,
        }}
      />

      {/* Tag Usage Guide */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900 text-base">Note: Document Template Tags</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-blue-800 mb-3">
            Templates must have ALL required tags to be found. Use the following tag combinations:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white p-3 rounded border border-blue-200">
              <p className="text-sm font-semibold text-gray-900 mb-2">Ad-hoc Documents (with "doc" tag):</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Quote: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">quote</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">doc</code></li>
                <li>• Policy: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">policy</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">doc</code></li>
                <li>• Invoice: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">invoice</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">doc</code></li>
                <li>• Farmer Profile: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">farmer</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">profile</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">doc</code></li>
                <li>• Product: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">product</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">doc</code></li>
              </ul>
            </div>
            <div className="bg-white p-3 rounded border border-blue-200">
              <p className="text-sm font-semibold text-gray-900 mb-2">Notification Templates (automatic):</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Email: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">policy</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">email</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">notification</code></li>
                <li>• SMS: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">policy</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">sms</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">notification</code></li>
                <li>• PDF Attachment: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">policy</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">attachment</code> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">notification</code></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <DocumentTemplateUploadDialog
        isOpen={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleUpload}
        loading={loading}
      />

      {templateToTest && (
        <TestTemplateDialog
          isOpen={testDialogOpen}
          onClose={() => {
            setTestDialogOpen(false);
            setTemplateToTest(null);
          }}
          templateName={templateToTest.name}
          templateType={templateToTest.type}
        />
      )}

      {/* Edit Dialog */}
      {editDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Edit Template</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Update the status and tags for {templateToEdit?.name}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditDialogOpen(false);
                  setTemplateToEdit(null);
                }}
                className="h-8 w-8 p-0"
              >
                <XIcon className="w-4 h-4" />
              </Button>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Status Field */}
              <div className="space-y-2">
                <label htmlFor="status" className="text-sm font-medium">Status</label>
                <Select
                  value={editForm.status}
                  options={Object.values(DocumentTemplateStatus).map(status => ({
                    value: status,
                    label: getStatusLabel(status),
                  }))}
                  onValueChange={(value) => setEditForm(prev => ({ ...prev, status: value as DocumentTemplateStatus }))}
                />
              </div>

              {/* Tags Field */}
              <div className="space-y-2">
                <label htmlFor="tags" className="text-sm font-medium">Tags (comma-separated)</label>
                <TextInput
                  id="tags"
                  placeholder="e.g. quote, doc"
                  value={editForm.tags}
                  onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                />
                <p className="text-sm text-gray-500">
                  Enter tags separated by commas. Examples: "quote, doc" or "policy, email, notification"
                </p>
              </div>

              {/* Current Tags Display */}
              {templateToEdit?.tags && templateToEdit.tags.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Current Tags:</label>
                  <div className="flex flex-wrap gap-2">
                    {templateToEdit.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditDialogOpen(false);
                    setTemplateToEdit(null);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
} 